import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import ServiceConversation from './ServiceConversation';
import ServiceRecallIntake from './ServiceRecallIntake';

type Context = { serviceCode: string; canRecall: boolean; intake: any; intakeFingerprint: string; version: number;
    recall?: { id: string; state: string; needsIntake: boolean; reason: string } };
const buttonClass = 'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-800 disabled:opacity-40';

export default function ServiceRecallWorkspace({ organizationId, requestId, children }: {
    organizationId: string; requestId: string; children: React.ReactNode;
}) {
    const callable = useMemo(() => httpsCallable(functions, 'manageServiceRecall'), []);
    const [context, setContext] = useState<Context | null>(null);
    const [intake, setIntake] = useState<any>({});
    const [reason, setReason] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [epoch, setEpoch] = useState(0);
    const call = useCallback(async (action: string, values: Record<string, unknown> = {}) =>
        (await callable({ action, organizationId, requestId, ...values })).data as any, [callable, organizationId, requestId]);
    const apply = useCallback((data: Context) => {
        setContext(data);
        setIntake(data.intake);
        setEditing(Boolean(data.recall?.state === 'active' && data.recall.needsIntake));
    }, []);
    useEffect(() => {
        let active = true;
        void call('load').then(data => { if (active) apply(data); }).catch(failure => {
            if (active) setError(failure.message || 'Recall status failed loading.');
        });
        return () => { active = false; };
    }, [call, apply]);
    useEffect(() => {
        if (context?.recall?.state !== 'active' || editing)
            return;
        let active = true;
        const refresh = () => {
            if (document.visibilityState === 'visible')
                void call('load').then(data => { if (active) apply(data); }).catch(() => {});
        };
        const timer = window.setInterval(refresh, 60000);
        window.addEventListener('focus', refresh);
        return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
    }, [context?.recall?.state, editing, call, apply]);
    const run = async (action: 'recall' | 'saveIntake') => {
        setBusy(true);
        setError('');
        try {
            await call(action, action === 'recall' ? { reason } : { intake, recallId: context?.recall?.id,
                version: context?.version, intakeFingerprint: context?.intakeFingerprint });
            apply(await call('load'));
            setConfirming(false);
            setEpoch(value => value + 1);
        } catch (failure: any) {
            setError(failure.message || 'Service recall failed.');
        } finally {
            setBusy(false);
        }
    };
    const recalled = context?.recall?.state === 'active';
    return <div className="space-y-6">
        {(context?.canRecall || recalled || error) && <section aria-label="Service recall" className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 text-slate-900 [color-scheme:light]">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 className="text-lg font-extrabold">{recalled ? 'Report Recalled — Review Client Intake' : 'Recall Delivered Report'}</h2>
                <p className="mt-1 text-sm text-slate-600">{recalled ? 'The client report is withdrawn. Update the saved intake, then generate, review, and deliver a fresh report.'
                    : 'Reopen this workflow using the existing client information. The current report will be withdrawn until you deliver its replacement.'}</p></div>
                {context?.canRecall && !confirming && <button type="button" onClick={() => setConfirming(true)} className={buttonClass}>Recall Report</button>}
                {recalled && !editing && <button type="button" disabled={busy} onClick={() => setEditing(true)} className={buttonClass}>Edit Saved Intake</button>}
            </div>
            {error && <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>}
            {confirming && <div className="space-y-3"><label className="block text-sm font-bold">Reason for recall<textarea rows={3} maxLength={4000}
                value={reason} disabled={busy} onChange={event => setReason(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 bg-white p-3 font-normal text-slate-900" /></label>
                <p className="text-sm text-slate-600">Intake, evidence, and messages are retained. Existing billing stays unchanged. Previously downloaded copies cannot be withdrawn.</p>
                <div className="flex gap-3"><button type="button" disabled={busy || !reason.trim()} onClick={() => void run('recall')}
                    className={`${buttonClass} border-amber-300 bg-amber-50`}>Recall and Reopen Intake</button>
                    <button type="button" disabled={busy} onClick={() => setConfirming(false)} className={buttonClass}>Cancel</button></div></div>}
            {recalled && editing && <div className="space-y-4"><fieldset disabled={busy}><ServiceRecallIntake code={context!.serviceCode} intake={intake} onChange={setIntake} /></fieldset>
                <p className="text-sm text-slate-600">Saving intake clears draft outputs and approvals. The client does not need to complete intake again.</p>
                <button type="button" disabled={busy} onClick={() => void run('saveIntake')} className={`${buttonClass} border-blue-600 text-blue-700`}>Save Intake and Continue Workflow</button>
            </div>}
            {recalled && <ServiceConversation organizationId={organizationId} requestId={requestId} consultantCanSend title="Clarify Details With Client" />}
        </section>}
        {context && !editing && <div key={epoch}>{children}</div>}
    </div>;
}
